import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from '../AppConstants';
import { UIRoutes } from '../UIRoutes';
import { AuthService } from '../service/auth.service';

@Component({
  selector: 'app-payment-status',
  templateUrl: './payment-status.component.html',
  styleUrls: ['./payment-status.component.css']
})
export class PaymentStatusComponent implements OnInit {
  public confirmationId;
  bookings: any;
  transactionStatus: string;
  isLoading = true;

  get slotBookings(): any[] {
    return (this.bookings || []).filter(booking => !this.isAddOn(booking));
  }

  get addOnBookings(): any[] {
    return (this.bookings || []).filter(booking => this.isAddOn(booking));
  }

  private isAddOn(booking): boolean {
    return booking.isAddOn === true || ['add-on', 'addon', 'accessory'].includes(
      String(booking.category || '').trim().toLowerCase()
    );
  }

  getAddOnQuantity(booking): number {
    let config = booking.config;
    if (typeof config === 'string') {
      try {
        config = JSON.parse(config);
      } catch {
        config = null;
      }
    }
    const quantity = Number(booking.quantity ?? config?.addOnQuantity ?? config?.accessoryQuantity);
    return Number.isInteger(quantity) && quantity > 0 ? quantity : null;
  }

  getAddOnUnitPrice(booking): number {
    if (booking.unitPrice != null && Number.isFinite(Number(booking.unitPrice)) && Number(booking.unitPrice) >= 0) {
      return Number(booking.unitPrice);
    }
    const quantity = this.getAddOnQuantity(booking);
    return quantity ? Number(booking.amount) / quantity : null;
  }

  constructor(private authService: AuthService, private route: ActivatedRoute, private router: Router) {
    console.log("In constructor inside payment status component before getting confirmationId");
    this.confirmationId = this.route.snapshot.paramMap.get('confirmationId');
    console.log("In constructor inside payment status component after getting confirmationId");
  }

  ngOnInit() {
    //this.confirmationId = sessionStorage.getItem(AppConstants.CONFIRMATION_ID);
    console.log("In ngOnInit inside payment status component before getting booking by confirmationId");
    console.log(this.confirmationId);
    this.getBookingByConfirmationId(this.confirmationId);
  }

  getBookingByConfirmationId(confirmationId) {
    this.isLoading = true;
    console.log(confirmationId);
    this.authService.getBookingByConfirmationId(confirmationId).subscribe(
      (response) => {
        this.bookings = response;
        this.isLoading = false;
        console.log(response);
        this.redirectAdminOnSuccess();
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  /**
   * When the payment was started from the admin booking dialog (marker set in browser storage),
   * a successful payment returns the admin to the admin bookings page instead of staying on
   * this shared status screen. The marker is one-shot: it is cleared once consumed.
   */
  redirectAdminOnSuccess() {
    const paymentSource = this.authService.getStoredItem(AppConstants.SESSION_STORAGE_PAYMENT_SOURCE);
    if (paymentSource !== AppConstants.PAYMENT_SOURCE_ADMIN_BOOKING) {
      return;
    }
    const status = this.getTransactionStatus();
    if (status === 'Success') {
      this.authService.removeStoredItem(AppConstants.SESSION_STORAGE_PAYMENT_SOURCE);
      // Preselect the booked product on the admin bookings page, and pass the confirmationId
      // so it can show the same success popup the cash flow shows.
      const productId = (this.bookings && this.bookings[0]) ? this.bookings[0].productId : null;
      this.router.navigate(
        ['/' + UIRoutes.ROUTING_MODULE_LANDING_PAGE + '/' + UIRoutes.ROUTING_MODULE_ADMIN_BOOKINGS_PAGE],
        { queryParams: { productId, confirmationId: this.confirmationId } }
      );
    } else if (status === 'Failed') {
      // Attempt concluded unsuccessfully: drop the one-shot marker so it can't misfire on a later booking.
      this.authService.removeStoredItem(AppConstants.SESSION_STORAGE_PAYMENT_SOURCE);
    }
  }

  getTransactionStatus() {
    if(this.bookings && this.bookings.length > 0) {
      if(this.bookings.length > 0 && (this.bookings[0].bookingStatus == 'Failed' || this.bookings[0].bookingStatus == 'Cancelled')) {
        return 'Failed';
      } else if(this.bookings.length > 0 && this.bookings[0].bookingStatus == 'Booked') {
        return 'Success';
      } else if(this.bookings.length > 0 && this.bookings[0].bookingStatus == 'WaitingForPayment') {
        return 'WaitingForPayment';
      } else {
        return this.bookings[0].bookingStatus;
      }
    }
  }

  getStatusClass(): string {
    const status = this.getTransactionStatus();

    if (status === 'Success') {
      return 'payment-success';
    }

    if (status === 'Failed') {
      return 'payment-failed';
    }

    if (status === 'WaitingForPayment') {
      return 'payment-waiting';
    }

    return 'payment-unknown';
  }

  getStatusIcon(): string {
    const status = this.getTransactionStatus();

    if (status === 'Success') {
      return 'bi-check-lg';
    }

    if (status === 'Failed') {
      return 'bi-x-lg';
    }

    if (status === 'WaitingForPayment') {
      return 'bi-hourglass-split';
    }

    return 'bi-receipt';
  }

  getStatusTitle(): string {
    const status = this.getTransactionStatus();

    if (status === 'Success') {
      return 'Payment Successful';
    }

    if (status === 'Failed') {
      return 'Payment Failed';
    }

    if (status === 'WaitingForPayment') {
      return 'Payment Pending';
    }

    return 'Checking Payment';
  }

  getStatusMessage(): string {
    const status = this.getTransactionStatus();

    if (status === 'Success') {
      return 'Your booking is confirmed. The details are listed below.';
    }

    if (status === 'Failed') {
      return 'The transaction could not be completed. Please review the booking status below.';
    }

    if (status === 'WaitingForPayment') {
      return 'The booking is waiting for payment confirmation.';
    }

    return 'Please wait while we fetch the latest booking status.';
  }

  getTotalAmount(): number {
    if (!this.bookings || this.bookings.length === 0) {
      return 0;
    }

    return this.bookings.reduce((sum, booking) => {
      return sum + (Number(booking?.amount) || 0) + (Number(booking?.platformFeeEndUser) || 0);
    }, 0);
  }

  getPlatformFeeEndUser(): number {
    if (!this.bookings || this.bookings.length === 0) {
      return 0;
    }

    return this.bookings.reduce((sum, booking) => sum + (Number(booking?.platformFeeEndUser) || 0), 0);
  }

  goToMyBookings(): void {
    this.router.navigate(['/' + UIRoutes.ROUTING_MODULE_LANDING_PAGE + '/' + UIRoutes.ROUTING_MODULE_TRANSACTIONS]);
  }

  browseMoreVenues(): void {
    this.router.navigate(['/' + UIRoutes.ROUTING_MODULE_PRODUCT_ALL]);
  }

  getStartTime(booking) {
    return booking.startTime;
  }

  getEndTime(booking) {
    return booking.endTime;
  }

  getAmount(booking) {
    return booking.amount;
  }

  getUserFullName(booking) {
    return booking.name;
  }

  getUserPhoneNumber(booking) {
    return booking.phoneNumber;
  }

  getUserName(booking) {
    return booking.email;
  }

  getBookingDateConverted(booking) {
    return booking.bookingDate;
  }

  getPaymentType(booking) {
    return booking?.paymentType;
  }

  getVenueName(booking) {
    return booking.venueName;
  }

}
